import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useContent } from "../context/SiteContext";
import { api, formatErr } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { QuoteForm } from "../components/site/QuoteFormDynamic";
import { PageHero } from "./PublicLayout";
import { Logo } from "../components/site/Brand";

import { PasswordInput } from "../components/site/PasswordInput";

export function QuotePage() {
  const c = useContent("quote_section");
  return (
    <div data-testid="quote-page">
      <PageHero white={c.title_white} blue={c.title_blue} desc={c.description} />
      <div className="max-w-3xl mx-auto px-5 lg:px-8 py-14"><QuoteForm /></div>
    </div>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatErr(err));
    }
    setBusy(false);
  };
  return (
    <AuthCard title="Forgot password">
      {sent ? (
        <p className="text-slate-300 text-center" data-testid="forgot-success">If an account exists for <b>{email}</b>, we've emailed a reset link. It expires in 1 hour. Check your spam folder too.</p>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <p className="text-sm text-slate-400">Enter your account email and we'll send you a link to reset your password.</p>
          <Input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 bg-[#040a1a] border-[#007bff]/35" placeholder="Email" data-testid="forgot-email-input" />
          <button disabled={busy} className="btn-electric w-full h-12" data-testid="forgot-submit-btn">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Send reset link"}</button>
        </form>
      )}
      <p className="mt-5 text-center text-sm"><Link to="/login" className="text-[#00d9ff]">Back to login</Link></p>
    </AuthCard>
  );
}

export function ResetPassword() {
  const loc = useLocation();
  const nav = useNavigate();
  const token = new URLSearchParams(loc.search).get("token") || "";
  const [pw, setPw] = useState({ a: "", b: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.a !== pw.b) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw.a });
      toast.success("Password updated. Please log in.");
      nav("/login");
    } catch (err) {
      toast.error(formatErr(err));
    }
    setBusy(false);
  };
  return (
    <AuthCard title="Choose a new password">
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <PasswordInput required minLength={6} placeholder="New password" value={pw.a} onChange={(e) => setPw({ ...pw, a: e.target.value })} className="h-12 bg-[#040a1a] border-[#007bff]/35" testid="reset-password-input" />
        <PasswordInput required minLength={6} placeholder="Confirm new password" value={pw.b} onChange={(e) => setPw({ ...pw, b: e.target.value })} className="h-12 bg-[#040a1a] border-[#007bff]/35" testid="reset-confirm-input" />
        <button disabled={busy || !token} className="btn-electric w-full h-12" data-testid="reset-submit-btn">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Update password"}</button>
        {!token && <p className="text-rose-300 text-sm text-center">This reset link is missing its token. Request a new one.</p>}
      </form>
    </AuthCard>
  );
}

function AuthCard({ title, children }) {
  return (
    <div className="min-h-screen pt-28 pb-16 px-5 grid place-items-center bg-[radial-gradient(ellipse_at_top,rgba(0,100,255,.25),transparent_65%)]" data-testid="auth-card-page">
      <div className="w-full max-w-md card-neon p-7 sm:p-9">
        <div className="text-center"><Logo className="text-3xl" /></div>
        <h1 className="font-display text-4xl text-metal text-center mt-4 mb-5">{title}</h1>
        {children}
      </div>
    </div>
  );
}

export default function AuthPage() {
  const loc = useLocation();
  const nav = useNavigate();
  const { login, register } = useAuth();
  const [mode, setMode] = useState(loc.pathname === "/register" ? "register" : "login");
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await login(f.email, f.password);
      else await register(f.name, f.email, f.password);
      nav("/dashboard");
    } catch (err) {
      toast.error(formatErr(err));
    }
    setBusy(false);
  };
  const cls = "h-12 bg-[#040a1a] border-[#007bff]/35";
  return (
    <div className="min-h-screen pt-28 pb-16 px-5 grid place-items-center bg-[radial-gradient(ellipse_at_top,rgba(0,100,255,.25),transparent_65%)]" data-testid="auth-page">
      <div className="w-full max-w-md card-neon p-7 sm:p-9">
        <div className="text-center"><Logo className="text-3xl" /></div>
        <div className="mt-6 grid grid-cols-2 p-1 rounded-xl bg-[#040a1a] border border-[#007bff]/25">
          {["login", "register"].map((m) => (
            <button key={m} onClick={() => setMode(m)} className={`py-2 rounded-lg font-cond font-bold uppercase tracking-wider ${mode === m ? "bg-[#007bff] text-white" : "text-slate-400"}`} data-testid={`auth-tab-${m}`}>
              {m === "login" ? "Login" : "Register"}
            </button>
          ))}
        </div>
        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="auth-form">
          {mode === "register" && <div className="space-y-2"><Label>Full name</Label><Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={cls} data-testid="auth-name-input" /></div>}
          <div className="space-y-2"><Label>Email</Label><Input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={cls} data-testid="auth-email-input" /></div>
          <div className="space-y-2"><Label>Password</Label><PasswordInput required minLength={6} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={cls} testid="auth-password-input" /></div>
          <button disabled={busy} className="btn-electric w-full h-12" data-testid="auth-submit-btn">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : mode === "login" ? "Login" : "Create account"}</button>
        </form>
        {mode === "login" && <p className="mt-3 text-center text-sm"><Link to="/forgot-password" className="text-[#00d9ff]" data-testid="forgot-password-link">Forgot your password?</Link></p>}
        <p className="mt-5 text-center text-sm text-slate-400">Grinder accounts are created by RushGrind2K admins. <Link to="/contact" className="text-[#00d9ff]">Contact us</Link></p>
      </div>
    </div>
  );
}
